// The right-hand pane of a split ChatView: an inline snippet — a live web
// view the agent is hosting in its container — rendered in a sandboxed
// iframe. The control bar (back-to-chat, title, reload, close) sits at the
// *bottom* so the iframe has a clean top edge and nothing collides with the
// chat header. The iframe remounts whenever the agent (`reloadNonce`) or the
// user (the local `manualNonce`) asks for a reload — with Vite HMR most
// reloads are invisible, these cover the rest.

import { useState } from 'react';
import type { InlineSnippetPayload } from '../../protocol/types';
import { ChevronLeftIcon, CloseIcon, ReloadIcon } from '../ui/Icons';

export function InlineSnippetPanel({
  snippet,
  reloadNonce,
  onClose,
  onShowChat,
}: {
  snippet: InlineSnippetPayload;
  reloadNonce: number;
  onClose: () => void;
  // Flips back to the transcript on mobile, where the panel is full-viewport.
  // Hidden at `md:` and up, where chat and snippet share the screen.
  onShowChat: () => void;
}) {
  const [manualNonce, setManualNonce] = useState(0);
  // A fresh `key` forces a clean iframe remount.
  const frameKey = `${snippet.token}:${reloadNonce + manualNonce}`;

  return (
    <div className="flex h-full flex-col bg-surface">
      <iframe
        key={frameKey}
        src={snippet.url}
        title={snippet.title}
        className="min-h-0 flex-1 border-0 bg-white"
        // allow-same-origin: the iframe is same-origin (`/_inline/`) and Vite's
        // HMR client + module scripts need it. No allow-top-navigation, so the
        // snippet can't navigate the chat away.
        sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-modals"
      />
      <div className="flex items-center gap-2 border-t border-divider px-3 py-2">
        <button
          type="button"
          onClick={onShowChat}
          aria-label="Back to chat"
          className="flex shrink-0 items-center gap-1 rounded-full bg-elevated py-1 pl-1.5 pr-2.5 text-xs text-text-secondary transition-colors hover:text-text-primary md:hidden"
        >
          <ChevronLeftIcon size={14} />
          Chat
        </button>
        <div className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
          {snippet.title}
        </div>
        <button
          type="button"
          onClick={() => setManualNonce((n) => n + 1)}
          aria-label="Reload live view"
          className="text-text-secondary transition-colors hover:text-text-primary"
        >
          <ReloadIcon size={15} />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close live view"
          className="text-text-secondary transition-colors hover:text-text-primary"
        >
          <CloseIcon size={16} />
        </button>
      </div>
    </div>
  );
}
